import type { BattleSnapshot } from '../../core';

export const STAGE_GEOMETRY = Object.freeze({
  width: 1280,
  height: 720,
  groundY: 620,
  leftBoundary: 80,
  rightBoundary: 1200,
});

export class StageRenderer {
  private readonly graphics: Phaser.GameObjects.Graphics;

  public constructor(graphics: Phaser.GameObjects.Graphics) {
    this.graphics = graphics;
  }

  public render(snapshot: BattleSnapshot): void {
    this.graphics.clear();
    this.renderFarBackground();
    this.renderOfficeMidground();
    this.renderFloor();
    this.renderContactShadows(snapshot);
  }

  /**
   * Layer 1: Far Background (Night sky, window structure, distant city skyline, wall clock 02:00)
   */
  private renderFarBackground(): void {
    const g = this.graphics;

    // Dark Base Wall (#0a0f1d)
    g.fillStyle(0x0a0f1d, 1);
    g.fillRect(0, 0, 1280, STAGE_GEOMETRY.groundY);

    // Large Office Window Pane (X: 180 to 1100, Y: 40 to 420)
    g.fillStyle(0x040814, 1);
    g.fillRect(180, 40, 920, 380);

    // Distant City Skyline Silhouette (Behind window)
    g.fillStyle(0x0a1224, 1);
    const buildings = [
      { x: 190, y: 180, w: 75, h: 240 },
      { x: 275, y: 140, w: 90, h: 280 },
      { x: 375, y: 220, w: 60, h: 200 },
      { x: 445, y: 110, w: 110, h: 310 },
      { x: 565, y: 190, w: 85, h: 230 },
      { x: 660, y: 130, w: 95, h: 290 },
      { x: 765, y: 170, w: 70, h: 250 },
      { x: 845, y: 100, w: 105, h: 320 },
      { x: 960, y: 160, w: 80, h: 260 },
      { x: 1050, y: 210, w: 45, h: 210 },
    ];
    for (const b of buildings) {
      g.fillRect(b.x, b.y, b.w, b.h);
    }

    // Sparse glowing windows in distant buildings
    g.fillStyle(0xfacc15, 0.35); // warm yellow
    g.fillRect(295, 170, 6, 8);
    g.fillRect(310, 210, 6, 8);
    g.fillRect(475, 140, 8, 10);
    g.fillRect(510, 190, 8, 10);
    g.fillRect(685, 160, 6, 8);
    g.fillRect(710, 230, 6, 8);
    g.fillRect(875, 130, 8, 10);
    g.fillRect(905, 180, 8, 10);
    g.fillRect(990, 200, 6, 8);

    g.fillStyle(0x38bdf8, 0.3); // cool cyan
    g.fillRect(215, 230, 6, 8);
    g.fillRect(330, 250, 6, 8);
    g.fillRect(490, 240, 8, 10);
    g.fillRect(880, 240, 8, 10);
    g.fillRect(920, 220, 8, 10);

    // Window Mullions / Frames
    g.lineStyle(4, 0x172238, 1);
    g.strokeRect(180, 40, 920, 380);
    // Vertical mullions
    g.lineBetween(410, 40, 410, 420);
    g.lineBetween(640, 40, 640, 420);
    g.lineBetween(870, 40, 870, 420);
    // Horizontal mullion
    g.lineBetween(180, 230, 1100, 230);

    // Wall Clock at Top-Right (X: 1160, Y: 110) showing 02:00
    this.renderWallClock(1160, 110, 24);
  }

  /**
   * Wall clock displaying 02:00
   */
  private renderWallClock(x: number, y: number, radius: number): void {
    const g = this.graphics;
    // Clock outer frame
    g.fillStyle(0x172238, 1);
    g.fillCircle(x, y, radius);
    g.lineStyle(2, 0x334155, 1);
    g.strokeCircle(x, y, radius);

    // Clock face
    g.fillStyle(0x0f172a, 1);
    g.fillCircle(x, y, radius - 3);

    // Hour marks (12, 3, 6, 9)
    g.fillStyle(0x94a3b8, 0.8);
    g.fillRect(x - 1, y - radius + 5, 2, 4); // 12
    g.fillRect(x + radius - 9, y - 1, 4, 2); // 3
    g.fillRect(x - 1, y + radius - 9, 2, 4); // 6
    g.fillRect(x - radius + 5, y - 1, 4, 2); // 9

    // Hands showing 02:00
    // Minute hand pointing to 12 (straight up)
    g.lineStyle(2, 0xe2e8f0, 1);
    g.lineBetween(x, y, x, y - radius + 7);

    // Hour hand pointing to 2 (60 deg from top: x + sin(60)*r, y - cos(60)*r)
    const hourLen = radius * 0.55;
    g.lineStyle(2.5, 0x38bdf8, 1);
    g.lineBetween(x, y, x + hourLen * 0.866, y - hourLen * 0.5);

    // Center pin
    g.fillStyle(0x38bdf8, 1);
    g.fillCircle(x, y, 2.5);
  }

  /**
   * Layer 2: Office Midground (Desks, monitors with subtle glow, chairs, office partitions)
   * Designed with strict low-contrast in the central fight box to preserve character readability.
   */
  private renderOfficeMidground(): void {
    const g = this.graphics;

    // Left Office Wall Partition / Whiteboard (X: 40..160, Y: 220..520)
    g.fillStyle(0x0e172a, 0.85);
    g.fillRect(40, 220, 110, 300);
    g.lineStyle(2, 0x1e293b, 1);
    g.strokeRect(40, 220, 110, 300);
    // Whiteboard inner area
    g.fillStyle(0x131e33, 0.7);
    g.fillRect(48, 230, 94, 180);
    // Whiteboard marker diagram lines
    g.lineStyle(1.5, 0x334155, 0.6);
    g.strokeRect(58, 250, 30, 20);
    g.lineBetween(88, 260, 105, 260);
    g.strokeRect(105, 250, 26, 20);
    g.lineBetween(73, 270, 73, 290);
    g.strokeRect(58, 290, 72, 22);

    // Right Office Cabinet / Server Rack (X: 1120..1240, Y: 240..620)
    g.fillStyle(0x0f172a, 0.9);
    g.fillRect(1120, 240, 110, 380);
    g.lineStyle(2, 0x1e293b, 1);
    g.strokeRect(1120, 240, 110, 380);
    // Cabinet drawer lines
    g.lineBetween(1120, 320, 1230, 320);
    g.lineBetween(1120, 400, 1230, 400);
    g.lineBetween(1120, 480, 1230, 480);
    g.lineBetween(1120, 560, 1230, 560);
    // Server / device status LEDs
    g.fillStyle(0x22c55e, 0.6); // green LED
    g.fillCircle(1135, 260, 2);
    g.fillCircle(1145, 260, 2);
    g.fillStyle(0x38bdf8, 0.6); // blue LED
    g.fillCircle(1135, 275, 2);
    g.fillStyle(0xef4444, 0.5); // red LED
    g.fillCircle(1145, 275, 2);

    // Midground Desks & Monitors (Subtle silhouettes, Y: 460..620)
    this.renderDeskPod(220, 480);
    this.renderDeskPod(540, 480);
    this.renderDeskPod(860, 480);
  }

  /**
   * Helper to render a desk pod with dual monitor silhouettes and office chair
   */
  private renderDeskPod(x: number, y: number): void {
    const g = this.graphics;

    // Chair silhouette behind desk
    g.fillStyle(0x0a101d, 0.9);
    // Chair backrest
    g.fillRoundedRect(x + 55, y - 45, 30, 45, 4);
    // Headrest
    g.fillRoundedRect(x + 60, y - 60, 20, 12, 3);
    // Chair pole
    g.fillRect(x + 68, y, 4, 30);

    // Desk Surface
    g.fillStyle(0x111c2e, 0.9);
    g.fillRect(x, y + 25, 140, 12);
    g.lineStyle(1.5, 0x1e2d44, 0.8);
    g.strokeRect(x, y + 25, 140, 12);

    // Desk Legs
    g.fillStyle(0x0d1524, 1);
    g.fillRect(x + 6, y + 37, 6, STAGE_GEOMETRY.groundY - (y + 37));
    g.fillRect(x + 128, y + 37, 6, STAGE_GEOMETRY.groundY - (y + 37));

    // Monitor 1 (Left: X + 15)
    g.fillStyle(0x080d17, 1);
    g.fillRect(x + 15, y - 25, 48, 36);
    g.lineStyle(1.5, 0x1e293b, 1);
    g.strokeRect(x + 15, y - 25, 48, 36);
    // Monitor 1 stand
    g.fillRect(x + 36, y + 11, 6, 14);
    // Screen glow (terminal / dashboard grid lines)
    g.fillStyle(0x081d33, 0.8);
    g.fillRect(x + 18, y - 22, 42, 30);
    g.fillStyle(0x38bdf8, 0.25);
    g.fillRect(x + 22, y - 18, 22, 2);
    g.fillRect(x + 22, y - 12, 30, 2);
    g.fillRect(x + 22, y - 6, 18, 2);
    g.fillRect(x + 22, y, 26, 2);

    // Monitor 2 (Right: X + 72)
    g.fillStyle(0x080d17, 1);
    g.fillRect(x + 72, y - 25, 48, 36);
    g.lineStyle(1.5, 0x1e293b, 1);
    g.strokeRect(x + 72, y - 25, 48, 36);
    // Monitor 2 stand
    g.fillRect(x + 93, y + 11, 6, 14);
    // Screen glow
    g.fillStyle(0x091c29, 0.8);
    g.fillRect(x + 75, y - 22, 42, 30);
    g.fillStyle(0x34d399, 0.22);
    g.fillRect(x + 79, y - 18, 28, 2);
    g.fillRect(x + 79, y - 12, 16, 2);
    g.fillRect(x + 79, y - 6, 32, 2);
    g.fillRect(x + 79, y, 20, 2);
  }

  /**
   * Layer 3: Floor (Ground boundary line, carpet texture fill)
   */
  private renderFloor(): void {
    const g = this.graphics;

    // Baseboard trim at Y=612..620
    g.fillStyle(0x131c2d, 1);
    g.fillRect(0, 612, 1280, 8);

    // Ground Edge line at Y=620
    g.lineStyle(2.5, 0x3b4c68, 1);
    g.lineBetween(0, STAGE_GEOMETRY.groundY, 1280, STAGE_GEOMETRY.groundY);

    // Office Carpet Fill (Y: 620..720)
    g.fillStyle(0x161e2e, 1);
    g.fillRect(0, STAGE_GEOMETRY.groundY, 1280, 100);

    // Carpet subtle tile grid pattern
    g.lineStyle(1, 0x1f2b3e, 0.5);
    for (let x = 0; x < 1280; x += 80) {
      g.lineBetween(x, STAGE_GEOMETRY.groundY, x - 40, 720);
    }
    g.lineBetween(0, 670, 1280, 670);
  }

  /**
   * Character Contact Shadow:
   * Presentation-only, projected onto the ground at Y=620.
   * REMAINS on the ground even when the character is airborne/jumping.
   */
  private renderContactShadows(snapshot: BattleSnapshot): void {
    const g = this.graphics;

    for (const character of snapshot.characters) {
      const footX = character.position.x;
      const airborneHeight = Math.max(0, STAGE_GEOMETRY.groundY - character.position.y);

      // Scale shadow based on airborne elevation while keeping it anchored at groundY
      const shadowAlpha = Math.max(0.18, 0.50 - airborneHeight * 0.0012);
      const shadowWidth = Math.max(28, 64 - airborneHeight * 0.08);
      const shadowHeight = Math.max(7, 14 - airborneHeight * 0.02);

      g.fillStyle(0x000000, shadowAlpha);
      g.fillEllipse(footX, STAGE_GEOMETRY.groundY + 2, shadowWidth, shadowHeight);
    }
  }
}
